import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TextT, Trash, Plus, Eye, ArrowSquareOut, Warning, CursorClick } from "@phosphor-icons/react";

const MAX_LEN = 25;

export default function MyTextAds() {
  const [ads, setAds] = useState([]);
  const [balance, setBalance] = useState(0);
  const [limit, setLimit] = useState(3);
  const [membership, setMembership] = useState("Free");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [text, setText] = useState("");
  const [targetUrl, setTargetUrl] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/text-ads/mine");
      setAds(data.ads || []);
      setBalance(Number(data.balance || 0));
      setLimit(Number(data.limit || 3));
      setMembership(data.membership || "Free");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const atLimit = ads.length >= limit;

  const submit = async (e) => {
    e.preventDefault();
    if (atLimit) { toast.error(`${membership} plan allows up to ${limit} text ads. Delete one or upgrade.`); return; }
    if (!text.trim()) { toast.error("Enter the ad copy first"); return; }
    if (text.length > MAX_LEN) { toast.error(`Ad text must be ${MAX_LEN} characters or fewer`); return; }
    if (!/^https?:\/\//i.test(targetUrl.trim())) { toast.error("Target URL must start with http:// or https://"); return; }
    setCreating(true);
    try {
      await api.post("/text-ads", { text: text.trim(), target_url: targetUrl.trim() });
      toast.success("Text ad created");
      setText(""); setTargetUrl("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreating(false); }
  };

  const remove = async (ad) => {
    if (!window.confirm("Delete this text ad? Any unspent impressions will be refunded.")) return;
    try {
      await api.delete(`/text-ads/${ad.id}`);
      toast.success("Text ad deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400">Advertising</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            My Text Ads
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            Short 25-character ads that appear underneath banner ads across the site. Buy impressions from the store.
          </p>
        </div>

        {/* Impressions balance */}
        <div data-testid="my-textads-balance" className="rounded-2xl border border-cyan-200 dark:border-cyan-500/30 bg-gradient-to-br from-white to-cyan-50 dark:from-slate-900 dark:to-slate-900 p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Available impressions</div>
            <div className="mt-1 text-4xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
              {balance.toLocaleString()}
            </div>
          </div>
          <a
            href="/buy-text-ads"
            data-testid="my-textads-buy-more"
            className="inline-flex items-center gap-2 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white font-black uppercase tracking-widest text-sm px-4 py-2"
          >
            <TextT size={14} weight="fill" /> Buy more <ArrowSquareOut size={12} weight="bold" />
          </a>
        </div>

        {/* Create form */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Create a new text ad</div>
            <span
              data-testid="my-textads-slot-usage"
              className={`inline-flex items-center gap-1 rounded-full text-[10px] font-black uppercase tracking-widest px-2.5 py-1 ${
                atLimit
                  ? "bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
              }`}
            >
              {ads.length} / {limit} slots · {membership}
            </span>
          </div>
          <form onSubmit={submit} className="space-y-2">
            <div>
              <input
                data-testid="my-textads-text-input"
                placeholder="Ad copy (max 25 characters)"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
              <div className="text-[10px] text-slate-400 mt-1 text-right">{text.length} / {MAX_LEN}</div>
            </div>
            <input
              data-testid="my-textads-url-input"
              type="url"
              placeholder="Target URL (https://…)"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
            />
            <button
              data-testid="my-textads-create-btn"
              type="submit"
              disabled={atLimit || creating || !text.trim() || !targetUrl.trim()}
              className={`inline-flex items-center gap-1 rounded-full text-sm font-bold px-4 py-2 text-white ${
                atLimit || creating || !text.trim() || !targetUrl.trim()
                  ? "bg-slate-400 dark:bg-slate-700 cursor-not-allowed"
                  : "bg-blue-700 hover:bg-blue-800"
              }`}
            >
              <Plus size={14} weight="bold" /> {creating ? "Creating…" : atLimit ? "Slots full" : "Create text ad"}
            </button>
          </form>
          <div className="mt-3 text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-1">
            <Warning size={14} weight="bold" className="text-amber-500 mt-0.5" />
            <span>Max <strong>{MAX_LEN} characters</strong>. Text ads appear in a colorful strip attached to the bottom of the banner across the site.</span>
          </div>
          {atLimit && (
            <div className="mt-3 rounded-lg border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-xs text-rose-700 dark:text-rose-300">
              You've hit the {membership} text ad limit ({limit}). Delete one below, or{" "}
              <a href="/buy-upgrades" className="font-bold underline">upgrade your plan</a> to unlock more slots.
            </div>
          )}
        </section>

        {/* Ad list */}
        <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Your text ads ({ads.length})</div>
          {loading ? (
            <div className="py-10 text-center text-slate-500 text-sm">Loading…</div>
          ) : ads.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">No text ads yet — add one above.</div>
          ) : (
            <div className="space-y-3">
              {ads.map((a) => (
                <div key={a.id} data-testid={`textad-row-${a.id}`} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3 flex items-center gap-4 flex-wrap">
                  <div className="rounded-md px-3 py-1.5 bg-gradient-to-r from-cyan-400 via-blue-400 to-fuchsia-400 text-white font-bold text-sm" title="How it appears on the site">
                    {a.text}
                  </div>
                  <div className="flex-1 min-w-0">
                    <a href={a.target_url} target="_blank" rel="noreferrer noopener" className="text-sm font-bold text-blue-700 dark:text-blue-400 hover:underline truncate block" title={a.target_url}>
                      {a.target_url}
                    </a>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                        <Eye size={11} weight="bold" /> {Number(a.impressions_served || 0).toLocaleString()} views
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                        <CursorClick size={11} weight="bold" /> {Number(a.clicks_served || 0).toLocaleString()} clicks
                      </span>
                      {a.impressions_served > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                          CTR {((Number(a.clicks_served || 0) / Math.max(1, Number(a.impressions_served || 0))) * 100).toFixed(2)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid={`textad-delete-${a.id}`}
                    onClick={() => remove(a)}
                    className="inline-flex items-center gap-1 rounded-full bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 text-xs font-bold px-3 py-1.5 hover:bg-rose-200 dark:hover:bg-rose-500/30"
                  >
                    <Trash size={12} weight="bold" /> Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
